import styled, { css, keyframes } from 'styled-components';
import { THEME } from '@/common/styles/theme';

export const SelectedProfileRoot = styled.div`
	min-height: 92px;
	padding: 16px 14px 16px 18px;
	display: grid;
	grid-template-columns: 62px minmax(0, 1fr) auto;
	gap: 13px;
	align-items: center;
	background: linear-gradient(
		135deg,
		rgba(255, 255, 255, 0.075),
		rgba(255, 255, 255, 0.025)
	);
	border-bottom: 1px solid rgba(246, 142, 255, 0.14);
	backdrop-filter: blur(18px);

	@media (max-width: ${THEME.mobileBreakpoint}) {
		min-height: 72px;
		padding: 10px ${THEME.mobileGutter};
		gap: 10px;

		${(props) =>
			props.$showBack
				? css`
						grid-template-columns: auto 48px minmax(0, 1fr) auto;
					`
				: css`
						grid-template-columns: 48px minmax(0, 1fr) auto;
					`}
	}
`;

export const ChatBackButton = styled.button`
	display: none;
	grid-column: 1;
	grid-row: 1;
	border: 0;
	border-radius: 10px;
	width: 36px;
	height: 36px;
	padding: 0;
	flex-shrink: 0;
	color: rgba(255, 245, 255, 0.92);
	background: rgba(255, 255, 255, 0.06);
	cursor: pointer;
	place-items: center;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		display: grid;
	}

	svg {
		width: 18px;
		height: 18px;
		fill: none;
		stroke: currentColor;
		stroke-width: 2.2;
		stroke-linecap: round;
		stroke-linejoin: round;
	}
`;

export const ProfileActions = styled.div`
	display: inline-flex;
	align-items: center;
	justify-content: flex-end;
	gap: 8px;
	justify-self: end;
	min-width: 0;
	margin-left: auto;
`;

export const ProfileAvatarWrap = styled.div`
	position: relative;
	width: 62px;
	height: 62px;
	flex-shrink: 0;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 48px;
		height: 48px;
	}
`;

export const ProfileCallDot = styled.span`
	position: absolute;
	right: -1px;
	bottom: 3px;
	width: 15px;
	height: 15px;
	border-radius: 50%;
	border: 3px solid #150c1f;
	transition:
		background 180ms ease,
		box-shadow 180ms ease;

	${({ $waveMode }) => {
		if ($waveMode === 'user') {
			return css`
				background: #38bdf8;
				box-shadow: 0 0 10px rgba(56, 189, 248, 0.7);
			`;
		}

		if ($waveMode === 'character') {
			return css`
				background: #ff83e9;
				box-shadow: 0 0 10px rgba(255, 131, 233, 0.7);
			`;
		}

		return css`
			background: #6b7280;
			box-shadow: 0 0 8px rgba(107, 114, 128, 0.45);
		`;
	}}
`;

export const ProfileAvatar = styled.img`
	width: 62px;
	height: 62px;
	border-radius: 50%;
	object-fit: cover;
	border: 1px solid rgba(255, 255, 255, 0.28);
	box-shadow: 0 0 20px rgba(255, 77, 242, 0.16);

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 48px;
		height: 48px;
	}
`;

export const ProfileMeta = styled.div`
	min-width: 0;
`;

export const ProfileName = styled.h3`
	margin: 0 0 5px;
	font-family: ${THEME.fontDisplay};
	font-size: 1.45rem;
	font-weight: 700;
	line-height: 1;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	color: ${THEME.text};

	@media (max-width: ${THEME.mobileBreakpoint}) {
		margin-bottom: 3px;
		font-size: 1.1rem;
	}
`;

export const ProfileOrigin = styled.p`
	margin: 0;
	color: rgba(255, 255, 255, 0.68);
	font-size: 0.94rem;
	line-height: 1.25;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		font-size: 0.8125rem;
	}
`;

export const ActiveCallPill = styled.div`
	justify-self: end;
	min-width: 148px;
	height: 44px;
	padding: 5px 6px 5px 13px;
	display: flex;
	align-items: center;
	gap: 10px;
	border-radius: 999px;
	backdrop-filter: blur(22px) saturate(130%);
	transition:
		border-color 180ms ease,
		background 180ms ease,
		box-shadow 180ms ease;

	${({ $waveMode }) => {
		if ($waveMode === 'user') {
			return css`
				border: 1px solid rgba(56, 189, 248, 0.38);
				background:
					radial-gradient(
						circle at 20% 0%,
						rgba(56, 189, 248, 0.2),
						transparent 48%
					),
					rgba(12, 34, 52, 0.62);
				box-shadow:
					inset 0 1px 0 rgba(255, 255, 255, 0.12),
					0 0 22px rgba(56, 189, 248, 0.2);
			`;
		}

		if ($waveMode === 'character') {
			return css`
				border: 1px solid rgba(255, 139, 239, 0.28);
				background:
					radial-gradient(
						circle at 20% 0%,
						rgba(255, 120, 237, 0.17),
						transparent 48%
					),
					rgba(44, 20, 50, 0.52);
				box-shadow:
					inset 0 1px 0 rgba(255, 255, 255, 0.12),
					0 0 22px rgba(221, 55, 255, 0.12);
			`;
		}

		return css`
			border: 1px solid rgba(255, 255, 255, 0.14);
			background: rgba(24, 24, 34, 0.52);
			box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
		`;
	}}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		min-width: 120px;
		height: 38px;
		padding: 4px 5px 4px 10px;
		gap: 8px;
	}
`;

export const CallButton = styled.button`
	position: relative;
	height: 42px;
	padding: 0 18px 0 12px;
	flex: 0 0 auto;
	display: inline-flex;
	align-items: center;
	gap: 10px;
	border: 1px solid rgba(255, 181, 249, 0.3);
	border-radius: 999px;
	color: #fff;
	background:
		radial-gradient(
			circle at 88% 18%,
			rgba(255, 190, 115, 0.28),
			transparent 42%
		),
		linear-gradient(135deg, rgba(176, 38, 255, 0.82), rgba(255, 77, 242, 0.62));
	box-shadow:
		inset 0 1px 0 rgba(255, 255, 255, 0.18),
		0 0 22px rgba(221, 55, 255, 0.2);
	backdrop-filter: blur(18px);
	cursor: pointer;
	transition:
		transform 180ms ease,
		border-color 180ms ease,
		box-shadow 180ms ease;

	&:hover,
	&:focus-visible {
		transform: translateY(-2px);
		border-color: rgba(255, 210, 251, 0.52);
		box-shadow:
			inset 0 1px 0 rgba(255, 255, 255, 0.22),
			0 0 30px rgba(230, 67, 255, 0.34);
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		height: 36px;
		padding: 0 10px;
		gap: 0;
	}
`;

export const CallButtonIcon = styled.span`
	width: 28px;
	height: 28px;
	flex: 0 0 28px;
	display: grid;
	place-items: center;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.14);
	box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.16);

	svg {
		width: 15px;
		height: 15px;
		fill: none;
		stroke: currentColor;
		stroke-width: 2;
		stroke-linecap: round;
		stroke-linejoin: round;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 24px;
		height: 24px;
		flex-basis: 24px;

		svg {
			width: 13px;
			height: 13px;
		}
	}
`;

export const CallButtonLabel = styled.span`
	font-size: 0.84rem;
	font-weight: 800;
	letter-spacing: 0.01em;
	white-space: nowrap;
	color: rgba(255, 247, 255, 0.96);

	@media (max-width: ${THEME.mobileBreakpoint}) {
		display: none;
	}
`;

export const ProfilePanelToggleArrow = styled.span`
	flex: 0 0 auto;
	width: 6px;
	height: 6px;
	border-top: 2px solid currentColor;
	border-right: 2px solid currentColor;
	transform: rotate(225deg);
	transition: transform 180ms ease;

	${(props) =>
		props.$panelOpen &&
		css`
			transform: rotate(45deg);
		`}
`;

export const ProfilePanelToggle = styled.button`
	border: 1px solid rgba(255, 255, 255, 0.12);
	border-radius: 999px;
	width: 32px;
	min-width: 32px;
	height: 32px;
	flex: 0 0 32px;
	padding: 0;
	color: rgba(255, 245, 255, 0.88);
	background: linear-gradient(
		135deg,
		rgba(255, 255, 255, 0.11),
		rgba(255, 255, 255, 0.035)
	);
	box-shadow: 0 0 18px rgba(255, 77, 242, 0.08);
	backdrop-filter: blur(16px);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition:
		border-color 160ms ease,
		box-shadow 160ms ease,
		background 160ms ease,
		color 160ms ease,
		visibility 160ms ease;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 30px;
		min-width: 30px;
		height: 30px;
		flex-basis: 30px;
	}

	&:hover,
	&:focus-visible {
		color: #ffffff;
		border-color: rgba(255, 119, 244, 0.34);
		background: linear-gradient(
			135deg,
			rgba(255, 77, 242, 0.2),
			rgba(255, 255, 255, 0.06)
		);
		box-shadow: 0 0 22px rgba(255, 77, 242, 0.16);
	}

	${(props) =>
		props.$panelOpen &&
		css`
			visibility: hidden;
			pointer-events: none;
			width: 0;
			min-width: 0;
			flex-basis: 0;
			margin: 0;
			padding: 0;
			border-width: 0;
			overflow: hidden;
		`}
`;

export const CallWave = styled.span`
	height: 20px;
	display: inline-flex;
	align-items: center;
	gap: 2px;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		height: 16px;
	}
`;

const wavePulse = keyframes`
	from {
		height: 5px;
		opacity: 0.62;
	}
	to {
		height: 19px;
		opacity: 1;
	}
`;

export const CallWaveBar = styled.i`
	width: 2px;
	height: 7px;
	border-radius: 999px;
	animation: ${wavePulse} 900ms ease-in-out infinite alternate;
	animation-play-state: ${({ $active }) => ($active ? 'running' : 'paused')};
	opacity: ${({ $active }) => ($active ? 1 : 0.35)};
	transition: opacity 180ms ease;

	${({ $waveMode }) => {
		if ($waveMode === 'user') {
			return css`
				background: linear-gradient(#38bdf8, #22d3ee);
				box-shadow: 0 0 8px rgba(56, 189, 248, 0.6);
			`;
		}

		if ($waveMode === 'character') {
			return css`
				background: linear-gradient(#ff83e9, #b855ff);
				box-shadow: 0 0 8px rgba(255, 80, 230, 0.6);
			`;
		}

		return css`
			background: rgba(255, 255, 255, 0.28);
			box-shadow: none;
		`;
	}}

	&:nth-child(2),
	&:nth-child(4) {
		animation-delay: -500ms;
	}

	&:nth-child(3) {
		animation-delay: -220ms;
	}
`;

export const CallTime = styled.time`
	min-width: 40px;
	color: rgba(255, 247, 255, 0.92);
	font-size: 0.78rem;
	font-variant-numeric: tabular-nums;
	font-weight: 750;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		min-width: 36px;
		font-size: 0.72rem;
	}
`;

export const HangupButton = styled.button`
	position: relative;
	width: 34px;
	height: 34px;
	flex: 0 0 34px;
	display: grid;
	place-items: center;
	border: 1px solid rgba(255, 149, 177, 0.42);
	border-radius: 50%;
	padding: 0;
	color: #fff;
	background: linear-gradient(
		145deg,
		rgba(255, 72, 130, 0.95),
		rgba(193, 24, 81, 0.88)
	);
	box-shadow: 0 0 17px rgba(255, 50, 111, 0.25);
	cursor: pointer;
	transition:
		transform 160ms ease,
		box-shadow 160ms ease;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 30px;
		height: 30px;
		flex-basis: 30px;

		svg {
			width: 16px;
			height: 16px;
		}
	}

	svg {
		width: 18px;
		height: 18px;
		fill: none;
		stroke: currentColor;
		stroke-width: 1.8;
		stroke-linecap: round;
		stroke-linejoin: round;
	}

	&:hover,
	&:focus-visible {
		transform: scale(1.06);
		box-shadow: 0 0 24px rgba(255, 50, 111, 0.42);
	}
`;
